import {
  BrandDesignSystemProvider,
  BrandFontLoader,
  BrandGuidelinesProvider,
  dimensionsForFormat,
  getTemplate,
  getTemplateMeta,
  listTemplateMetas,
} from '@magig/creative/react';
import {
  type BrandDesignSystem,
  type BrandGuidelines,
  type CreativeAssetFormat,
  DEFAULT_BRAND_DESIGN_SYSTEM,
  DEFAULT_BRAND_GUIDELINES,
} from '@magig/schemas';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { brandBrainQuery, brandsQuery } from '../lib/queries.js';

// Dev-only template preview playground. Renders the live React tree
// for any template against any brand's saved guidelines + the chosen
// format, with side-rail controls to swap brand/template/format and
// a JSON fill editor for hot-swapping copy.
//
// Use this instead of the export-to-PNG → screenshot loop when
// debugging brand chrome (logo, weight, gradients, etc.). Browser
// DevTools can inspect the rendered HTML/CSS directly.
//
// Gated behind import.meta.env.DEV — production build excludes the
// route entirely via tree-shaking on the env check.

export const Route = createFileRoute('/dev/preview')({
  component: DevPreview,
});

function DevPreview() {
  if (!import.meta.env.DEV) {
    return (
      <div className="p-6 font-mono text-[13px] text-[var(--ink)]">
        dev-only route. only loaded in development builds.
      </div>
    );
  }
  return <Playground />;
}

function Playground() {
  const brandsList = useQuery({ ...brandsQuery() });
  const allTemplates = useMemo(() => listTemplateMetas(), []);

  // Default selections: first brand, first STATIC template (Remotion
  // video templates would need their own Player path; v1 is static-
  // only — the Tile + StaticPreview path covers the brand-chrome
  // debug we built this for).
  const staticTemplates = useMemo(
    () => allTemplates.filter((m) => getTemplate(m.key) !== null),
    [allTemplates],
  );

  const [brandId, setBrandId] = useState<string | null>(null);
  const [templateKey, setTemplateKey] = useState<string>(staticTemplates[0]?.key ?? 'hook-card-v1');
  const [format, setFormat] = useState<CreativeAssetFormat>('feed_1x1');
  // Fill JSON is keyed by templateKey so switching templates
  // synchronously falls back to the new template's defaults without a
  // setState→render round-trip (the previous shape would otherwise
  // get passed to the new Component for one frame, crashing with
  // "cannot read properties of undefined" inside auto-size helpers).
  const [fillJsonByKey, setFillJsonByKey] = useState<Record<string, string>>({});

  // Seed brand on first load.
  useEffect(() => {
    if (brandId === null && brandsList.data?.[0]?.id) {
      setBrandId(brandsList.data[0].id);
    }
  }, [brandId, brandsList.data]);

  const brandQuery = useQuery({
    ...brandBrainQuery(brandId),
    enabled: typeof brandId === 'string',
    staleTime: 30_000,
  });
  const guidelines: BrandGuidelines =
    brandQuery.data?.brandBrain?.guidelines ?? DEFAULT_BRAND_GUIDELINES;
  // s248 — DS-aware primitives (BrandTitle / BrandBody / BrandEyebrow)
  // fall through to DEFAULT_BRAND_DESIGN_SYSTEM without this provider,
  // so the playground used to show generic Acme-default typography
  // regardless of which brand was selected. Same envelope shape the
  // /render/creative/$id route uses.
  const designSystem: BrandDesignSystem =
    brandQuery.data?.brandBrain?.designSystem ?? DEFAULT_BRAND_DESIGN_SYSTEM;

  const meta = useMemo(() => getTemplateMeta(templateKey), [templateKey]);
  const staticTemplate = useMemo(() => getTemplate(templateKey), [templateKey]);
  const Component = staticTemplate?.Component ?? null;

  // Re-seed the format if the current pick isn't supported by the
  // newly chosen template.
  useEffect(() => {
    const eligible = meta?.formats ?? [];
    if (eligible.length > 0 && !eligible.includes(format)) {
      setFormat(eligible[0] ?? 'feed_1x1');
    }
  }, [meta, format]);

  // Editor displays either the user's keyed edit OR the canonical
  // JSON for this template's defaults. Empty string in the per-key
  // map = "user hasn't edited" = show defaults.
  const defaultsJson = useMemo(
    () => (meta?.defaults ? JSON.stringify(meta.defaults, null, 2) : ''),
    [meta],
  );
  const fillJson = fillJsonByKey[templateKey] ?? defaultsJson;

  const { parsedFill, fillError } = useMemo(() => {
    if (fillJson.trim().length === 0) {
      return { parsedFill: meta?.defaults, fillError: null };
    }
    try {
      return { parsedFill: JSON.parse(fillJson), fillError: null };
    } catch (e) {
      return {
        parsedFill: meta?.defaults,
        fillError: e instanceof Error ? e.message : String(e),
      };
    }
  }, [fillJson, meta]);

  const canvas = dimensionsForFormat(format);
  // Cap the rendered canvas to a sensible preview height so the user
  // doesn't have to scroll past a 1920px-tall story.
  const MAX_PREVIEW_HEIGHT = 720;
  const scale = Math.min(1, MAX_PREVIEW_HEIGHT / canvas.height);
  const scaledWidth = Math.round(canvas.width * scale);
  const scaledHeight = Math.round(canvas.height * scale);

  return (
    <div className="flex h-screen w-screen bg-[var(--paper)]">
      {/* Left rail: pickers + fill editor */}
      <aside className="flex w-[360px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-[var(--line)] bg-white p-4">
        <header>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            ↘ dev · template preview
          </p>
          <h1 className="mt-1 font-display text-[18px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            playground
          </h1>
          <p className="mt-1 text-[11px] text-[var(--ink)]/55">
            render any template against any brand's saved guidelines. inspect HTML/CSS in DevTools.
          </p>
        </header>

        <section className="flex flex-col gap-1">
          <label
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70"
            htmlFor="brand-picker"
          >
            brand
          </label>
          <select
            id="brand-picker"
            value={brandId ?? ''}
            onChange={(e) => setBrandId(e.target.value || null)}
            className="rounded border border-[var(--line)] bg-white px-2 py-1.5 text-[13px] text-[var(--ink)]"
          >
            <option value="">(default neutral guidelines)</option>
            {(brandsList.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} · {b.slug}
              </option>
            ))}
          </select>
          {brandQuery.isLoading ? (
            <span className="font-mono text-[9.5px] text-[var(--ink)]/50">
              loading brand guidelines…
            </span>
          ) : brandQuery.error ? (
            <span className="font-mono text-[9.5px] text-[var(--pulse)]">
              error loading brand — using defaults
            </span>
          ) : null}
        </section>

        <section className="flex flex-col gap-1">
          <label
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70"
            htmlFor="template-picker"
          >
            template ({staticTemplates.length} static)
          </label>
          <select
            id="template-picker"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            className="rounded border border-[var(--line)] bg-white px-2 py-1.5 text-[13px] text-[var(--ink)]"
          >
            {staticTemplates.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label} · {m.key}
              </option>
            ))}
          </select>
        </section>

        <section className="flex flex-col gap-1">
          <label
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70"
            htmlFor="format-picker"
          >
            format
          </label>
          <select
            id="format-picker"
            value={format}
            onChange={(e) => setFormat(e.target.value as CreativeAssetFormat)}
            className="rounded border border-[var(--line)] bg-white px-2 py-1.5 text-[13px] text-[var(--ink)]"
          >
            {(meta?.formats ?? ['feed_1x1']).map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <span className="font-mono text-[9.5px] text-[var(--ink)]/50">
            canvas {canvas.width}×{canvas.height} · scaled to fit
          </span>
        </section>

        <section className="flex flex-1 flex-col gap-1">
          <label
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70"
            htmlFor="fill-editor"
          >
            fill (json — edit and re-render)
          </label>
          <textarea
            id="fill-editor"
            value={fillJson}
            onChange={(e) =>
              setFillJsonByKey((prev) => ({ ...prev, [templateKey]: e.target.value }))
            }
            spellCheck={false}
            className="min-h-[320px] flex-1 resize-y rounded border border-[var(--line)] bg-[var(--paper)] p-2 font-mono text-[11px] leading-snug text-[var(--ink)]"
          />
          {fillError ? (
            <span className="font-mono text-[10px] text-[var(--pulse)]">
              json parse error: {fillError} — rendering last-valid fill
            </span>
          ) : null}
          <button
            type="button"
            onClick={() =>
              setFillJsonByKey((prev) => {
                const next = { ...prev };
                delete next[templateKey];
                return next;
              })
            }
            className="self-end rounded border border-[var(--line)] bg-white px-2 py-1 font-mono text-[10px] text-[var(--ink)]/70 hover:text-[var(--ink)]"
          >
            ↻ reset fill to defaults
          </button>
        </section>

        <footer className="border-t border-[var(--line)] pt-3 font-mono text-[10px] text-[var(--ink)]/45">
          guidelines source: {guidelines.source ?? '(none)'}
          {guidelines.scrapedAt ? ` · scraped ${guidelines.scrapedAt.slice(0, 10)}` : null}
        </footer>
      </aside>

      {/* Right pane: rendered template at-scale */}
      <main className="flex flex-1 items-center justify-center overflow-auto bg-[var(--ink)]/[0.05] p-6">
        {!Component ? (
          <div className="max-w-[420px] rounded-lg border-[1.5px] border-amber-400/70 bg-amber-50 p-4 font-mono text-[11px] text-amber-900">
            no static component for "{templateKey}". video templates aren't supported in v1 of the
            playground.
          </div>
        ) : (
          <div
            style={{ width: scaledWidth, height: scaledHeight }}
            className="overflow-hidden shadow-[6px_6px_0_var(--ink)]/15"
          >
            <div
              style={{
                width: canvas.width,
                height: canvas.height,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              {/* s241 — load the brand's fonts (Plus Jakarta Sans,
                  Instrument Serif, etc.) at the document head. Without
                  this, every render falls through to system-ui /
                  browser-default-serif, regardless of what the spec
                  says — the bug that was making the playground's CTA
                  render in Times instead of the brand's sans. */}
              <BrandFontLoader guidelines={guidelines} />
              <BrandGuidelinesProvider guidelines={guidelines}>
                <BrandDesignSystemProvider designSystem={designSystem}>
                  <Component fill={parsedFill} format={format} />
                </BrandDesignSystemProvider>
              </BrandGuidelinesProvider>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
